import type { CSSProperties } from 'react';
import styled from 'styled-components';
import { theme } from 'styled-tools';

import BadgeIcon from '@ui/badgeIcon';
import { Icon } from '@ui/icons';

import type { PreviewMilestone } from '../../../../src/types/route-preview.ts';
import {
  CALLOUT_GAP,
  DESTINATION_BADGE_TOP,
  DESTINATION_PIN_HEIGHT,
  DESTINATION_PIN_WIDTH,
  PREVIEW_BADGE_SIZE,
} from './calloutPlacement.ts';

const Badge = styled.div<{ $kind: PreviewMilestone['kind']; $completeArtwork: boolean }>`
  --preview-badge-size: calc(${PREVIEW_BADGE_SIZE}px * var(--preview-badge-scale, 1));
  --preview-pin-width: calc(${DESTINATION_PIN_WIDTH}px * var(--preview-badge-scale, 1));
  --preview-pin-height: calc(${DESTINATION_PIN_HEIGHT}px * var(--preview-badge-scale, 1));
  --preview-badge-top: calc(${DESTINATION_BADGE_TOP}px * var(--preview-badge-scale, 1));
  position: relative;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  width: ${props => (props.$kind === 'destination' ? `var(--preview-pin-width, ${DESTINATION_PIN_WIDTH}px)` : `var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px)`)};
  height: ${props => (props.$kind === 'destination' ? `var(--preview-pin-height, ${DESTINATION_PIN_HEIGHT}px)` : `var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px)`)};
  border: ${props =>
    props.$kind === 'stop'
      ? `2px solid ${props.theme.colors.primary}`
      : props.$kind === 'train' || props.$kind === 'bus'
        ? `0.75px solid ${props.theme.colors.miscBadgeIconStroke}`
        : 'none'};
  border-radius: ${theme('radii.full', '50%')};
  background: ${props =>
    props.$kind === 'origin' || props.$kind === 'destination'
      ? 'transparent'
      : props.$kind === 'stop'
        ? props.theme.colors.background
        : props.$kind === 'security'
          ? props.theme.colors.categoryBadgeSecurity
          : props.$kind === 'train' || props.$kind === 'bus'
            ? (props.theme.colors[
                props.$kind === 'train' ? 'categoryBadgeWayfindingTrain' : 'categoryBadgeWayfindingBus'
              ] ?? props.theme.colors.categoryBadgeWayfindingTransit)
            : props.theme.colors.primary};
  color: ${props => (props.$kind === 'stop' ? props.theme.colors.primary : props.theme.colors.miscBadgeIconColor)};
  font-family: ${theme('fonts.default')};
  font-weight: 700;
  pointer-events: none;

  /* Venue badge artwork supplies its own background and outline. */
  &:has(.injected-svg) {
    background: ${props => (props.$completeArtwork ? 'transparent' : undefined)};
  }
`;

const OriginArtwork = styled.svg`
  width: var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px);
  height: var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px);
  color: ${theme('colors.primary')};
  /* Preserve antialiased pixels where the circle meets the SVG boundary. */
  && {
    overflow: visible;
  }
`;

const DestinationArtwork = styled.svg`
  position: absolute;
  inset: 0;
  && {
    overflow: visible;
  }
  width: var(--preview-pin-width, ${DESTINATION_PIN_WIDTH}px);
  height: var(--preview-pin-height, ${DESTINATION_PIN_HEIGHT}px);
  fill: ${theme('colors.miscSelectedbgColor', '#FFFFFF')};
  stroke: ${theme('colors.miscSelectedbgStroke', '#C7C5CC')};
`;

const DestinationBadge = styled.div<{ $completeArtwork: boolean; $icon: string }>`
  position: absolute;
  display: grid;
  place-items: center;
  inset-block-start: var(--preview-badge-top, ${DESTINATION_BADGE_TOP}px);
  inset-inline-start: calc(50% - var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px) / 2);
  width: var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px);
  height: var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px);
  border-radius: ${theme('radii.full', '50%')};
  /* Square venue artwork is clipped to the circular pin badge. */
  overflow: hidden;
  background: ${props => {
    const category = props.$icon.split('.')[0];
    const color = `categoryBadge${category.charAt(0).toUpperCase()}${category.slice(1)}`;
    return props.theme.colors[color] ?? props.theme.colors.primary;
  }};

  &:has(.injected-svg) {
    background: ${props => (props.$completeArtwork ? 'transparent' : undefined)};
  }
`;

const Callout = styled.div<{ $singleLine?: boolean }>`
  position: absolute;
  box-sizing: border-box;
  width: max-content;
  max-width: var(--preview-callout-width, 260px);
  inset-block-start: var(--preview-callout-y, 50%);
  transform: translateY(var(--preview-callout-translate-y, -50%));
  inset-inline-start: calc(100% + ${CALLOUT_GAP}px);
  padding-block: 6px;
  padding-inline: 8px;
  border-radius: ${theme('radii.md', '4px')};
  background: ${theme('colors.background')};
  color: ${theme('colors.primaryText')};
  box-shadow: 0 2.296px 4.591px 0 rgba(0, 0, 0, 0.1);
  font-size: 12px;
  font-weight: ${props => (props.$singleLine ? 700 : 400)};
  line-height: 16px;
  overflow-wrap: anywhere;
  text-align: start;

  [data-callout-side='inline-start'] & {
    inset-inline-start: auto;
    inset-inline-end: calc(100% + ${CALLOUT_GAP}px);
  }

  [data-collision-side] & {
    left: var(--preview-callout-x);
    right: auto;
  }

  span,
  strong {
    display: block;
  }
`;

const Fallback = styled.span`
  display: grid;
  place-items: center;
  font-size: 16px;
  > span {
    width: var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px);
    height: var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px);
  }
`;

const StopNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  text-align: center;
  /* Optical alignment of Roboto numerals within the circle. */
  transform: translateY(0.5px);
`;

type RoutePreviewMarkerProps = {
  milestone: PreviewMilestone;
  direction?: 'ltr' | 'rtl';
  style?: CSSProperties;
};

/** Decorative map duplicate; milestone names are supplied separately to the semantic preview host. */
export default function RoutePreviewMarker({ milestone, direction = 'ltr', style }: RoutePreviewMarkerProps) {
  const transit = milestone.kind === 'train' || milestone.kind === 'bus';
  const fallback = (
    <Fallback>
      <BadgeIcon
        name={milestone.kind === 'origin' || milestone.kind === 'destination' ? 'my.location.arrow' : 'info'}
        size={PREVIEW_BADGE_SIZE}
        backgroundColor="transparent"
        aria-hidden="true"
      />
      {(milestone.fallbackLabel || (!milestone.callout && milestone.kind === 'facility')) && (
        <Callout data-preview-callout>
          <strong>{milestone.fallbackLabel ?? milestone.label}</strong>
        </Callout>
      )}
    </Fallback>
  );
  return (
    <Badge
      $kind={milestone.kind}
      $completeArtwork={!transit && milestone.artworkFormat === 'badge'}
      aria-hidden="true"
      dir={direction}
      style={style}
      data-preview-milestone={milestone.id}
    >
      {milestone.kind === 'destination' ? (
        <>
          <DestinationArtwork viewBox="0 0 32.491 42" data-preview-destination-pin aria-hidden="true" focusable="false">
            <path
              strokeWidth="1.58491"
              d="M29.6999 22.7391L29.6997 22.7394C28.4698 24.655 26.8096 26.2613 24.9066 28.0881C24.4964 28.4817 24.0781 28.8707 23.6486 29.2701L23.6481 29.2705L23.6464 29.272L23.6379 29.2799L23.6362 29.2815L23.619 29.2975C20.9447 31.784 18.021 34.5023 16.2455 39.2966C14.4735 34.5097 11.5574 31.797 8.88754 29.3135L8.87233 29.2993L8.87051 29.2976L8.86227 29.2899L8.86044 29.2882C8.42483 28.883 8.00047 28.4882 7.58448 28.0886L7.58433 28.0884C5.68126 26.2614 4.02093 24.6552 2.79105 22.7395L2.79083 22.7392C1.4287 20.6199 0.79248 18.4085 0.79248 15.712C0.79248 11.7336 2.39033 7.99397 5.30717 5.17225C8.22505 2.3495 12.105 0.79248 16.2453 0.79248C20.3856 0.79248 24.2656 2.3495 27.1835 5.17226C30.1002 7.99397 31.6981 11.7336 31.6981 15.712C31.6981 18.4085 31.062 20.6197 29.6999 22.7391Z"
            />
          </DestinationArtwork>
          <DestinationBadge $completeArtwork={milestone.artworkFormat === 'badge'} $icon={milestone.icon}>
            <Icon
              name={milestone.icon}
              cdn
              preserveArtworkColors
              width={`var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px)`}
              height={`var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px)`}
              fallback={fallback}
            />
          </DestinationBadge>
        </>
      ) : milestone.kind === 'origin' ? (
        <OriginArtwork
          data-preview-origin
          width={`var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px)`}
          height={`var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px)`}
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="12" cy="12" r="12" fill="currentColor" />
          <path
            fill="white"
            d="M11.475 18.9C11.3 18.8375 11.1438 18.7438 11.0063 18.6188C10.1938 17.8688 9.475 17.1375 8.85 16.425C8.225 15.7125 7.70313 15.0219 7.28438 14.3532C6.86563 13.6844 6.54688 13.0407 6.32813 12.4219C6.10938 11.8032 6 11.2125 6 10.65C6 8.77504 6.60313 7.28129 7.80938 6.16879C9.01563 5.05629 10.4125 4.50004 12 4.50004C13.5875 4.50004 14.9844 5.05629 16.1906 6.16879C17.3969 7.28129 18 8.77504 18 10.65C18 11.2125 17.8906 11.8032 17.6719 12.4219C17.4531 13.0407 17.1344 13.6844 16.7156 14.3532C16.2969 15.0219 15.775 15.7125 15.15 16.425C14.525 17.1375 13.8063 17.8688 12.9938 18.6188C12.8563 18.7438 12.7 18.8375 12.525 18.9C12.35 18.9625 12.175 18.9938 12 18.9938C11.825 18.9938 11.65 18.9625 11.475 18.9ZM12 10.5C12.4125 10.5 12.7656 10.3532 13.0594 10.0594C13.3531 9.76567 13.5 9.41254 13.5 9.00004C13.5 8.58754 13.3531 8.23442 13.0594 7.94066C12.7656 7.64691 12.4125 7.50004 12 7.50004C11.5875 7.50004 11.2344 7.64691 10.9406 7.94066C10.6469 8.23442 10.5 8.58754 10.5 9.00004C10.5 9.41254 10.6469 9.76567 10.9406 10.0594C11.2344 10.3532 11.5875 10.5 12 10.5ZM13.4906 13.9313C13.9594 13.7188 14.375 13.4188 14.7375 13.0313C14.8 12.9688 14.8469 12.8969 14.8781 12.8157C14.9094 12.7344 14.925 12.65 14.925 12.5625C14.925 12.425 14.8906 12.3 14.8219 12.1875C14.7531 12.075 14.6625 11.9813 14.55 11.9063C14.15 11.6938 13.7375 11.5313 13.3125 11.4188C12.8875 11.3063 12.45 11.25 12 11.25C11.55 11.25 11.1125 11.3063 10.6875 11.4188C10.2625 11.5313 9.85 11.6938 9.45 11.9063C9.3375 11.9688 9.25 12.0563 9.1875 12.1688C9.125 12.2813 9.09375 12.4063 9.09375 12.5438C9.09375 12.6313 9.10625 12.7157 9.13126 12.7969C9.15625 12.8782 9.2 12.9563 9.2625 13.0313C9.625 13.4188 10.0406 13.7188 10.5094 13.9313C10.9781 14.1438 11.475 14.25 12 14.25C12.525 14.25 13.0219 14.1438 13.4906 13.9313Z"
          />
        </OriginArtwork>
      ) : milestone.stop ? (
        <StopNumber data-preview-stop-number>{milestone.stop}</StopNumber>
      ) : (
        <Icon
          name={milestone.icon}
          cdn={!transit}
          preserveArtworkColors
          width={`var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px)`}
          height={`var(--preview-badge-size, ${PREVIEW_BADGE_SIZE}px)`}
          fallback={fallback}
        />
      )}
      {milestone.callout && (
        <Callout data-preview-callout $singleLine={milestone.kind === 'security'}>
          {milestone.callout.context && <span>{milestone.callout.context}</span>}
          {milestone.kind === 'security' ? (
            <span>{milestone.callout.primary}</span>
          ) : (
            <strong>
              {milestone.callout.journey ? (
                <>
                  <bdi>{milestone.callout.journey.from}</bdi>{' '}
                  <bdi dir="ltr" data-preview-journey-arrow>
                    {direction === 'rtl' ? '←' : '→'}
                  </bdi>{' '}
                  <bdi>{milestone.callout.journey.to}</bdi>
                </>
              ) : (
                milestone.callout.primary
              )}
            </strong>
          )}
          {milestone.callout.secondary && <span>{milestone.callout.secondary}</span>}
        </Callout>
      )}
    </Badge>
  );
}
